import { useEffect, useState, useRef } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { showAlert } from '@/components/ui/GlobalAlert';
import { formatApiError } from '@/lib/utils';
import { showConfirm } from '@/components/ui/GlobalConfirm';
import {
  FileText, Loader2, FileSignature, Download, Save,
  Eye, ChevronRight, X, RefreshCw, Sparkles, Upload, Trash2, Star, Printer
} from 'lucide-react';

/* Descarga un HTML como .doc (Word/Google Docs lo abren con formato). Sin
   dependencias: envuelve el HTML en el sobre de Word y fuerza la descarga. */
function descargarComoWord(html, nombreBase) {
  const nombre = (nombreBase || 'documento')
    .trim()
    .replace(/[^\w\sáéíóúñÁÉÍÓÚÑ.-]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 80) || 'documento';
  const doc =
    '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
    'xmlns:w="urn:schemas-microsoft-com:office:word" ' +
    'xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8">' +
    `<title>${nombre}</title>` +
    "<style>@page{margin:2.5cm}body{font-family:'Times New Roman',serif;font-size:12pt;line-height:1.6;color:#000}</style>" +
    `</head><body>${html}</body></html>`;
  const blob = new Blob(['﻿', doc], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${nombre}.doc`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

/* Abre una ventana de impresión con solo el documento → el usuario elige
   "Guardar como PDF" en el diálogo del navegador. */
function imprimirDocumento(html, titulo) {
  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(
    '<html><head><meta charset="utf-8">' +
    `<title>${titulo || 'Documento'}</title>` +
    "<style>@page{margin:2.5cm}body{font-family:'Times New Roman',serif;font-size:12pt;line-height:1.6;color:#000;padding:1cm}</style>" +
    `</head><body>${html}</body></html>`
  );
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 350);
}

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/40 transition-all";

/* ─── Modal generador de documentos ──────────────────────────── */
function ModalGenerador({ onClose }) {
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [expedienteId, setExpedienteId] = useState('');
  const [plantillaId, setPlantillaId] = useState('');
  const [htmlDoc, setHtmlDoc] = useState('');
  const [cargandoDoc, setCargandoDoc] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [err, setErr] = useState('');
  const canvasRef = useRef(null);

  useEffect(() => {
    api.get('/api/clientes/').then((r) => setClientes(r.data)).catch(() => {});
    api.get('/api/demandas/modelos').then((r) => setPlantillas(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (clienteId) {
      api.get('/api/expedientes/')
        .then((r) => setExpedientes(r.data.filter((e) => e.cliente_id === clienteId || !e.cliente_id)))
        .catch(() => {});
    }
  }, [clienteId]);

  const cargarPlantilla = async (id) => {
    if (!clienteId) { setErr('Seleccioná un cliente primero'); return; }
    setPlantillaId(id); setErr(''); setCargandoDoc(true); setHtmlDoc(''); setGuardadoOk(false);
    try {
      const r = await api.post('/api/demandas/generar', {
        modelo_id: id,
        cliente_id: clienteId,
      });
      setHtmlDoc(r.data.html);
      setTitulo(id.replace('.docx', '').replace(/^\d+-?\s*/, ''));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al generar el documento'));
    } finally { setCargandoDoc(false); }
  };

  const guardar = async () => {
    const contenido = canvasRef.current?.innerHTML || htmlDoc;
    if (!contenido || !clienteId) { setErr('Generá un documento primero'); return; }
    const nombreDoc = titulo.trim() || 'Documento';
    setGuardando(true); setErr('');
    try {
      await api.post('/api/demandas/guardar', {
        cliente_id: clienteId,
        expediente_id: expedienteId || null,
        html: contenido,
        titulo: nombreDoc,
      });
      setGuardadoOk(true);
      setTimeout(() => setGuardadoOk(false), 3000);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al guardar en la bóveda'));
    } finally { setGuardando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#050505] flex flex-col">
      {/* Header del generador */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[rgba(201,164,76,0.12)] bg-[#0A0A0A] flex-shrink-0">
        <div className="flex items-center gap-3">
          <FileSignature className="w-5 h-5 text-[#C9A44C]" />
          <span className="text-sm font-medium">Generador de documentos jurídicos</span>
        </div>
        <div className="flex items-center gap-2">
          {guardadoOk && (
            <span className="text-xs text-green-400 flex items-center gap-1">
              <Save className="w-3.5 h-3.5" /> Guardado en bóveda
            </span>
          )}
          {htmlDoc && (
            <button onClick={guardar} disabled={guardando}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-[#070707] font-semibold disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
              {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Guardar en bóveda
            </button>
          )}
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0] p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Panel izquierdo: configuración */}
        <div className="w-72 flex-shrink-0 border-r border-[rgba(201,164,76,0.1)] bg-[#0A0A0A] flex flex-col overflow-y-auto">
          <div className="p-4 space-y-3">
            {err && (
              <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 text-red-400 text-xs">{err}</div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] block mb-1.5">Cliente *</label>
              <select className={IC + ' text-xs'} value={clienteId} onChange={(e) => { setClienteId(e.target.value); setHtmlDoc(''); setPlantillaId(''); }}>
                <option value="" className="bg-[#141414]">Seleccionar cliente…</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id} className="bg-[#141414]">
                    {c.apellido ? `${c.apellido.toUpperCase()}, ${c.nombre}` : c.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] block mb-1.5">Expediente (opcional)</label>
              <select className={IC + ' text-xs'} value={expedienteId} onChange={(e) => setExpedienteId(e.target.value)} disabled={!clienteId}>
                <option value="" className="bg-[#141414]">Sin expediente</option>
                {expedientes.map((e) => (
                  <option key={e.id} value={e.id} className="bg-[#141414]">{e.caratula}</option>
                ))}
              </select>
            </div>
            {htmlDoc && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] block mb-1.5">Nombre del documento</label>
                <input className={IC + ' text-xs'} value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Nombre para guardar…" />
              </div>
            )}
          </div>

          {/* Lista de plantillas */}
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C] mb-2.5">Plantillas disponibles</p>
            {plantillas.length === 0 ? (
              <p className="text-xs text-[#555]">Sin plantillas cargadas</p>
            ) : (
              <div className="space-y-1.5">
                {plantillas.map((p) => (
                  <button key={p.id} onClick={() => cargarPlantilla(p.id)} disabled={!clienteId || cargandoDoc}
                    className={`w-full text-left flex items-center gap-2.5 p-2.5 rounded-lg border transition-all text-xs disabled:opacity-40 ${
                      plantillaId === p.id
                        ? 'border-[#C9A44C]/40 bg-[#C9A44C]/10 text-[#C9A44C]'
                        : 'border-white/5 bg-white/[0.02] text-[#ccc] hover:border-[#C9A44C]/20 hover:bg-white/[0.04]'
                    }`}>
                    <FileSignature className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate flex-1">{p.nombre}</span>
                    {p.tipo === 'custom' && (
                      <Star className="w-3 h-3 text-[#C9A44C]/60 flex-shrink-0" />
                    )}
                    {plantillaId === p.id && cargandoDoc && (
                      <Loader2 className="w-3 h-3 animate-spin ml-auto flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Panel derecho: canvas A4 */}
        <div className="flex-1 overflow-y-auto bg-[#1a1a1a] p-6 flex items-start justify-center">
          {cargandoDoc ? (
            <div className="flex flex-col items-center gap-3 mt-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#C9A44C]" />
              <p className="text-sm text-[#888]">Procesando plantilla…</p>
            </div>
          ) : !htmlDoc ? (
            <div className="flex flex-col items-center gap-3 mt-20 text-center">
              <FileText className="w-12 h-12 text-[#555]" />
              <p className="text-[#666] text-sm">
                {clienteId
                  ? 'Seleccioná una plantilla del menú izquierdo'
                  : 'Primero seleccioná un cliente'}
              </p>
            </div>
          ) : (
            /* Canvas A4 editable */
            <div
              className="w-full max-w-[800px] bg-white text-black rounded-lg shadow-2xl overflow-hidden"
              style={{ minHeight: '1123px' }}>
              {/* Toolbar del canvas */}
              <div className="bg-[#f5f5f5] border-b border-gray-200 px-4 py-2 flex items-center gap-3 flex-wrap">
                <span className="text-xs text-gray-500 font-medium">Modo edición:</span>
                <button onClick={() => document.execCommand('bold')}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 font-bold">B</button>
                <button onClick={() => document.execCommand('italic')}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 italic">I</button>
                <button onClick={() => document.execCommand('underline')}
                  className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 underline">U</button>
                <div className="h-4 w-px bg-gray-300" />
                <button onClick={() => cargarPlantilla(plantillaId)}
                  className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200">
                  <RefreshCw className="w-3 h-3" /> Restaurar
                </button>
              </div>
              {/* Contenido editable */}
              <div
                ref={canvasRef}
                contentEditable
                suppressContentEditableWarning
                className="p-10 outline-none min-h-[1050px]"
                style={{
                  fontFamily: '"Times New Roman", Times, serif',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#222',
                }}
                dangerouslySetInnerHTML={{ __html: htmlDoc }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Plantillas con IA (documentos generados por IA) ─────────── */
function PlantillaIA({ clientes, expedientes }) {
  const [clienteId, setClienteId] = useState('');
  const [expedienteId, setExpedienteId] = useState('');
  const [tipo, setTipo] = useState('');
  const [instrucciones, setInstrucciones] = useState('');
  const [resultado, setResultado] = useState('');
  const [cargando, setCargando] = useState(false);
  const [err, setErr] = useState('');

  const generar = async () => {
    if (!tipo || !instrucciones) { setErr('Completá el tipo e instrucciones'); return; }
    setErr(''); setCargando(true); setResultado('');
    try {
      const r = await api.post('/api/asistente/generar-documento', {
        tipo, instrucciones, expediente_id: expedienteId || null, cliente_id: clienteId || null,
      });
      setResultado(r.data.html);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al generar con IA'));
    } finally { setCargando(false); }
  };

  return (
    <div className="space-y-3">
      {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 text-red-400 text-xs">{err}</div>}
      <div className="grid grid-cols-2 gap-3">
        <select className={IC} value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
          <option value="" className="bg-[#141414]">Cliente (opcional)</option>
          {clientes.map((c) => <option key={c.id} value={c.id} className="bg-[#141414]">{c.nombre} {c.apellido}</option>)}
        </select>
        <input className={IC} placeholder="Tipo de documento" value={tipo} onChange={(e) => setTipo(e.target.value)} />
      </div>
      <textarea className={IC + ' resize-none'} rows={4} placeholder="Instrucciones para la IA (ej: Contrato de honorarios para cliente Pérez, causa laboral…)"
        value={instrucciones} onChange={(e) => setInstrucciones(e.target.value)} />
      <button onClick={generar} disabled={cargando || !tipo || !instrucciones}
        className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-lg text-[#070707] font-semibold disabled:opacity-40"
        style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
        {cargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {cargando ? 'Generando…' : 'Generar con IA'}
      </button>
      {resultado && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#C9A44C]">Documento generado</span>
            <div className="flex items-center gap-2">
              <button onClick={() => descargarComoWord(resultado, tipo)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-[#070707] font-semibold"
                style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
                <Download className="w-3.5 h-3.5" /> Descargar Word
              </button>
              <button onClick={() => imprimirDocumento(resultado, tipo)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[rgba(201,164,76,0.3)] text-[#C9A44C] hover:bg-[#C9A44C]/10 transition-all">
                <Printer className="w-3.5 h-3.5" /> Imprimir / PDF
              </button>
            </div>
          </div>
          <div className="bg-white text-black rounded-lg p-8 text-sm overflow-y-auto max-h-[500px]"
            style={{ fontFamily: '"Times New Roman", serif', lineHeight: '1.6' }}
            dangerouslySetInnerHTML={{ __html: resultado }} />
        </div>
      )}
    </div>
  );
}

/* ─── Página principal Documentos ─────────────────────────────── */
export default function Documentos() {
  const [plantillas, setPlantillas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerador, setShowGenerador] = useState(false);
  const [tab, setTab] = useState('plantillas'); // plantillas | ia
  const [subiendo, setSubiendo] = useState(false);
  const [errUpload, setErrUpload] = useState('');
  const fileInputRef = useRef(null);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [p, c, e] = await Promise.allSettled([
        api.get('/api/demandas/modelos'),
        api.get('/api/clientes/'),
        api.get('/api/expedientes/'),
      ]);
      if (p.status === 'fulfilled') setPlantillas(p.value.data || []);
      if (c.status === 'fulfilled') setClientes(c.value.data);
      if (e.status === 'fulfilled') setExpedientes(e.value.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { cargarDatos(); }, []);

  const subirPlantilla = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      setErrUpload('Solo se permiten archivos .docx');
      return;
    }
    setSubiendo(true); setErrUpload('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post('/api/demandas/modelos/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await cargarDatos();
    } catch (err) {
      setErrUpload(formatApiError(err.response?.data?.detail, 'Error al subir la plantilla'));
    } finally {
      setSubiendo(false);
      e.target.value = '';
    }
  };

  const eliminarCustom = async (id, nombre) => {
    if (!(await showConfirm(`¿Eliminar la plantilla "${nombre}"? Esta acción no se puede deshacer.`, { titulo: 'Eliminar plantilla' }))) return;
    try {
      await api.delete(`/api/demandas/modelos/custom/${id}`);
      setPlantillas((p) => p.filter((x) => x.id !== id));
    } catch (err) {
      showAlert(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <DashboardLayout title="Documentos jurídicos">
      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-white/[0.03] p-1 rounded-xl w-fit border border-white/5">
        {[
          { id: 'plantillas', label: 'Plantillas .docx', icon: FileSignature },
          { id: 'ia', label: 'Generar con IA', icon: Sparkles },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
              tab === id
                ? 'bg-[#C9A44C] text-[#070707] font-semibold'
                : 'text-[#888] hover:text-[#F8F6F0]'
            }`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === 'plantillas' && (
        <>
          {/* Acciones */}
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                className="hidden"
                onChange={subirPlantilla}
              />
              <button
                onClick={() => { setErrUpload(''); fileInputRef.current?.click(); }}
                disabled={subiendo}
                className="flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg border border-[rgba(201,164,76,0.3)] text-[#C9A44C] hover:bg-[#C9A44C]/10 transition-all disabled:opacity-50">
                {subiendo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {subiendo ? 'Subiendo...' : 'Subir mi plantilla'}
              </button>
              {errUpload && <span className="text-xs text-red-400">{errUpload}</span>}
            </div>
            <button onClick={() => setShowGenerador(true)}
              className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-lg text-[#070707] font-semibold"
              style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
              <FileSignature className="w-4 h-4" /> Abrir generador
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
          ) : plantillas.length === 0 ? (
            <div className="text-center py-20 text-[#666]">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No hay plantillas disponibles</p>
              <p className="text-xs mt-1 text-[#555]">Subí tu propia plantilla .docx con el botón de arriba</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {plantillas.map((p) => (
                <div key={p.id}
                  className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5 hover:border-[#C9A44C]/30 transition-all group relative">
                  {/* Badge custom */}
                  {p.tipo === 'custom' && (
                    <div className="absolute top-3 right-3 flex items-center gap-1">
                      <button
                        onClick={() => eliminarCustom(p.id, p.nombre)}
                        className="p-1 text-[#555] hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#C9A44C]/15 border border-[#C9A44C]/25 text-[#C9A44C] flex items-center gap-1">
                        <Star className="w-2.5 h-2.5" /> mía
                      </span>
                    </div>
                  )}
                  <button className="w-full text-left" onClick={() => setShowGenerador(true)}>
                    <div className="w-10 h-10 rounded-lg bg-[#C9A44C]/10 flex items-center justify-center mb-3 group-hover:bg-[#C9A44C]/20 transition-all">
                      <FileSignature className="w-5 h-5 text-[#C9A44C]" />
                    </div>
                    <div className="text-sm font-medium mb-1 pr-16">{p.nombre}</div>
                    <div className="flex items-center gap-1 mt-2 text-[#C9A44C] text-[11px]">
                      <Eye className="w-3.5 h-3.5" /> Generar documento
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'ia' && (
        <div className="bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#C9A44C]" />
            <h3 className="text-sm">Generador con IA — Asistente legal</h3>
          </div>
          <PlantillaIA clientes={clientes} expedientes={expedientes} />
        </div>
      )}

      {showGenerador && <ModalGenerador onClose={() => setShowGenerador(false)} />}
    </DashboardLayout>
  );
}
